import { ReactElement } from 'react';
// next
import NextLink from 'next/link';
// @mui
import {
  Box,
  Breadcrumbs as MUIBreadcrumbs,
  BreadcrumbsProps as MUIBreadcrumbsProps,
  Link,
  Typography,
} from '@mui/material';
import useResponsive from '../hooks/useResponsive';

// ----------------------------------------------------------------------

export type TLink = {
  href?: string;
  name: string;
  icon?: ReactElement;
};

export interface BreadCrumbsProps extends MUIBreadcrumbsProps {
  links: TLink[];
  activeLast?: boolean;
}

export default function Breadcrumbs({ links, activeLast = false, ...other }: BreadCrumbsProps) {
  const currentLink = links[links.length - 1].name;
  const isMobile = useResponsive('down', 'sm');

  const listDefault = links.map((link) => <LinkItem key={link.name} link={link} />);

  const listActiveLast = links.map((link) => (
    <div key={link.name}>
      {link.name !== currentLink ? (
        <LinkItem link={link} />
      ) : (
        <Typography
          variant={!isMobile ? 'body2' : 'caption'}
          sx={{
            maxWidth: 260,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            color: 'text.disabled',
            textOverflow: 'ellipsis',
          }}
        >
          {currentLink}
        </Typography>
      )}
    </div>
  ));

  return (
    <MUIBreadcrumbs separator={'/'} {...other}>
      {activeLast ? listDefault : listActiveLast}
    </MUIBreadcrumbs>
  );
}

// ----------------------------------------------------------------------

type LinkItemProps = {
  link: TLink;
};

function LinkItem({ link }: LinkItemProps) {
  const { href = '', name, icon } = link;
  const isMobile = useResponsive('down', 'sm');

  return (
    <NextLink href={href} passHref>
      <Link
        key={name}
        variant={!isMobile ? 'body2' : 'caption'}
        sx={{
          lineHeight: 2,
          display: 'flex',
          alignItems: 'center',
          color: 'text.primary',
          '& > div': { display: 'inherit' },
        }}
      >
        {icon && <Box sx={{ mr: 1, '& svg': { width: 20, height: 20 } }}>{icon}</Box>}
        {name}
      </Link>
    </NextLink>
  );
}
